<template>
  <div
    class="rounded"
    :class="{ 'w-[320px]': apps.length >= MULTI_COLUMN_THRESHOLD, 'w-auto': apps.length < MULTI_COLUMN_THRESHOLD }"
  >
    <div v-if="showSearch" class="search-container pa-2">
      <CSearch
        v-model="searchQuery"
        :placeholder="t('canvasUI.canvasBuilder.components.AppGrid.searchApps')"
        size="small"
      />
    </div>

    <div
      class="pa-2 overflow-y-auto box-content"
      :class="{ 'pt-2': showSearch }"
      :style="{
        height: showSearch ? '288px' : 'auto',
        minHeight: displayedApps.length === 0 ? '100px' : 'auto',
      }"
    >
      <div
        v-if="displayedApps.length > 0"
        class="grid gap-3"
        :class="{
          'grid-cols-3': apps.length >= MULTI_COLUMN_THRESHOLD,
          'grid-cols-2': apps.length === 2,
          'grid-cols-1': apps.length === 1,
        }"
        :style="{ minHeight: displayedApps.length <= MULTI_COLUMN_THRESHOLD ? '80px' : 'auto' }"
      >
        <div
          v-for="app in displayedApps"
          :key="app.name"
          class="flex flex-col items-center justify-start cursor-pointer hover:bg-primary5/20 rounded-1 transition-all"
          @click="emit('click', app)"
        >
          <div
            class="mb-3 flex items-center justify-center w-11 h-11 rounded-1 bg-primary6 hover:bg-primary5 transition-all"
          >
            <CIcon
              class="max-w-full max-h-full w-auto h-auto object-contain"
              :color="themeVars.primary"
              :fa-type="getAppIconForDisplay(app).includes('fa-kit') ? 'fa-kit' : 'far'"
              :icon="getAppIconForDisplay(app)"
              size="20"
            />
          </div>
          <span
            v-tooltip="app.app_metadata?.display_name || app.app_metadata?.name"
            class="text-xs text-center font-medium line-clamp-2"
          >
            {{ app.app_metadata?.display_name || app.app_metadata?.name }}
          </span>
        </div>
      </div>
      <div v-else class="flex items-center justify-center h-full">
        <span class="text-sm color-text2">{{ t('canvasUI.canvasBuilder.components.AppGrid.noResults') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { orderBy } from 'lodash-es'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CSearch from '@lib/components/CSearch/CSearch.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import tooltipDirective from '@lib/directives/tooltip'
import type { AppFile } from '@lib/types/app'

const props = defineProps<{
  apps: AppFile[]
  getAppIcon?: (app: AppFile) => string
}>()

const emit = defineEmits<{
  (e: 'click', app: AppFile): void
}>()

const SEARCH_THRESHOLD = 9
const MULTI_COLUMN_THRESHOLD = 3

const searchQuery = ref('')
const showSearch = computed(() => props.apps.length > SEARCH_THRESHOLD)

const { t } = useI18n()
const themeVars = useThemeVars()
const vTooltip = tooltipDirective

const getAppIconForDisplay = (app: AppFile) => {
  if (props.getAppIcon) {
    return props.getAppIcon(app)
  }
  return app.app_metadata?.icon || 'fa-kit fa-regular-rocket-circle-question'
}

const searchFilteredApps = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.apps
  }

  const query = searchQuery.value.toLowerCase().trim()
  return props.apps.filter(
    ({ app_metadata }) =>
      (app_metadata?.display_name || app_metadata?.name || '').toLowerCase().includes(query) ||
      (app_metadata?.name || '').toLowerCase().includes(query) ||
      (app_metadata?.description && app_metadata?.description.toLowerCase().includes(query)),
  )
})

const displayedApps = computed(() => {
  return orderBy(
    searchFilteredApps.value,
    [
      ({ app_metadata }) => (app_metadata?.order_override !== undefined ? 0 : 1), // First sort by whether app has order_override
      ({ app_metadata }) =>
        app_metadata?.order_override !== undefined
          ? app_metadata?.order_override
          : (app_metadata?.display_name || app_metadata?.name || '').toLowerCase(),
    ],
    ['asc', 'asc'],
  )
})
</script>
